"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const api_external_contract_js_1 = require("@tableau/api-external-contract-js");
const api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
const uuid_1 = require("uuid");
const ApiShared_1 = require("../../../ApiShared");
const ExternalToInternalEnumMappings_1 = require("../../EnumMappings/ExternalToInternalEnumMappings");
const GetDataModels_1 = require("../../Models/GetDataModels");
const GetDataTableReaderModels_1 = require("../../Models/GetDataTableReaderModels");
const DataValueFactory_1 = require("../../Utils/DataValueFactory");
const GetDataService_1 = require("../GetDataService");
const ServiceImplBase_1 = require("./ServiceImplBase");
class GetDataServiceImpl extends ServiceImplBase_1.ServiceImplBase {
    constructor() {
        super(...arguments);
        // The extension/embedding instance uses the same cache for all DataTableReaders.
        this.viewDataTableCacheId = uuid_1.v4();
    }
    get serviceName() {
        return "get-data-service" /* GetData */;
    }
    getMaxRowLimit() {
        return 10000;
    }
    getViewDataTableCacheId() {
        return this.viewDataTableCacheId;
    }
    getLimitedMaxRows(requestedRows, rowCountLimit) {
        return requestedRows > 0 && requestedRows < rowCountLimit ? requestedRows : rowCountLimit;
    }
    getUnderlyingDataAsync(visualId, getType, ignoreAliases, ignoreSelection, includeAllColumns, columnsToIncludeById, maxRows, includeDataValuesOption, applyWorksheetFormatting) {
        // Create all of our parameters
        const summaryData = getType === GetDataService_1.GetDataType.Summary;
        const functionName = summaryData ? 'getSummaryDataAsync' : 'getUnderlyingDataAsync';
        const verb = summaryData ? api_internal_contract_js_1.VerbId.GetDataSummaryData : api_internal_contract_js_1.VerbId.GetUnderlyingData;
        const requestMaxRows = verb === api_internal_contract_js_1.VerbId.GetUnderlyingData ? this.getLimitedMaxRows(maxRows, this.getMaxRowLimit() + 1) : maxRows;
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: functionName,
        };
        parameters[api_internal_contract_js_1.ParameterId.VisualId] = visualId;
        parameters[api_internal_contract_js_1.ParameterId.IgnoreAliases] = ignoreAliases;
        parameters[api_internal_contract_js_1.ParameterId.IgnoreSelection] = ignoreSelection;
        parameters[api_internal_contract_js_1.ParameterId.IncludeAllColumns] = includeAllColumns;
        parameters[api_internal_contract_js_1.ParameterId.ColumnsToIncludeById] = this.verifyIncludeColumnArray(columnsToIncludeById);
        parameters[api_internal_contract_js_1.ParameterId.MaxRows] = requestMaxRows;
        parameters[api_internal_contract_js_1.ParameterId.ShowDataTableFormat] = ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption);
        parameters[api_internal_contract_js_1.ParameterId.ApplyWorksheetFormatting] = applyWorksheetFormatting;
        return this.execute(verb, parameters).then((response) => {
            const responseData = response.result;
            return this.processResultsTable(responseData.data, responseData.isSummary);
        });
    }
    getSummaryDataReaderAsync(visualId, pageRowCount, ignoreAliases, ignoreSelection, includeAllColumns, columnsToIncludeById, includeDataValuesOption, applyWorksheetFormatting) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getSummaryDataReaderAsync',
            [api_internal_contract_js_1.ParameterId.ViewDataTableCacheId]: this.getViewDataTableCacheId(),
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
            [api_internal_contract_js_1.ParameterId.PageRowCount]: pageRowCount,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.IgnoreSelection]: ignoreSelection,
            [api_internal_contract_js_1.ParameterId.IncludeAllColumns]: includeAllColumns,
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
            [api_internal_contract_js_1.ParameterId.ApplyWorksheetFormatting]: applyWorksheetFormatting,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetDataSummaryDataReader, parameters).then((response) => {
            const responseData = response.result;
            return new GetDataTableReaderModels_1.DataTableReader(responseData.id, responseData.totalRowCount, pageRowCount, this._registryId);
        });
    }
    getSummaryColumnsInfoAsync(visualId) {
        // Create all the parameters for GetDataType of Summary with 1 row, and only native values
        // Then return just the columns
        const verb = api_internal_contract_js_1.VerbId.GetDataSummaryData;
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getSummaryColumnsInfoAsync',
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: true,
            [api_internal_contract_js_1.ParameterId.IgnoreSelection]: true,
            [api_internal_contract_js_1.ParameterId.IncludeAllColumns]: true,
            [api_internal_contract_js_1.ParameterId.MaxRows]: 1,
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: api_internal_contract_js_1.ApiShowDataTableFormat.NativeValuesOnly,
        };
        return this.execute(verb, parameters).then((response) => {
            const underlyingDataTable = response.result;
            const dataTable = underlyingDataTable.data;
            const columns = dataTable.headers.map((h) => new GetDataModels_1.Column(h.fieldCaption, h.fieldName, h.dataType, h.isReferenced, h.index));
            return columns;
        });
    }
    getSelectedMarksAsync(visualId) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getSelectedMarksAsync',
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetSelectedMarks, parameters).then((response) => {
            const responseData = response.result;
            return {
                data: responseData.data.map((table) => this.processResultsTable(table, true)),
            };
        });
    }
    getHighlightedMarksAsync(visualId) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getHighlightedMarksAsync',
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetHighlightedMarks, parameters).then((response) => {
            const responseData = response.result;
            return {
                data: responseData.data.map((table) => this.processResultsTable(table, true)),
            };
        });
    }
    getDataSourceDataAsync(dataSourceId, ignoreAliases, maxRows, columnsToInclude, columnsToIncludeById, includeDataValuesOption) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getDataSourceDataAsync',
            [api_internal_contract_js_1.ParameterId.DataSourceId]: dataSourceId,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.MaxRows]: this.getLimitedMaxRows(maxRows, this.getMaxRowLimit() + 1),
            [api_internal_contract_js_1.ParameterId.ColumnsToInclude]: this.verifyIncludeColumnArray(columnsToInclude),
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetDataSourceData, parameters).then((response) => {
            const responseData = response.result;
            return this.processResultsTable(responseData.data, false);
        });
    }
    getLogicalTableDataAsync(datasourceId, logicalTableId, ignoreAliases, maxRows, columnsToInclude, columnsToIncludeById, includeDataValuesOption) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getLogicalTableDataAsync',
            [api_internal_contract_js_1.ParameterId.ColumnsToInclude]: columnsToInclude,
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.DataSourceId]: datasourceId,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.LogicalTableId]: logicalTableId,
            [api_internal_contract_js_1.ParameterId.MaxRows]: this.getLimitedMaxRows(maxRows, this.getMaxRowLimit() + 1),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetLogicalTableData, parameters).then((response) => {
            const responseData = response.result;
            return this.processResultsTable(responseData.data, false);
        });
    }
    getLogicalTableDataReaderAsync(datasourceId, logicalTableId, pageRowCount, ignoreAliases, columnsToIncludeById, includeDataValuesOption) {
        pageRowCount = this.getLimitedMaxRows(pageRowCount, this.getMaxRowLimit());
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getLogicalTableDataReaderAsync',
            [api_internal_contract_js_1.ParameterId.ViewDataTableCacheId]: this.getViewDataTableCacheId(),
            [api_internal_contract_js_1.ParameterId.DataSourceId]: datasourceId,
            [api_internal_contract_js_1.ParameterId.LogicalTableId]: logicalTableId,
            [api_internal_contract_js_1.ParameterId.PageRowCount]: pageRowCount,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetLogicalTableDataReader, parameters).then((response) => {
            const responseData = response.result;
            return new GetDataTableReaderModels_1.DataTableReader(responseData.id, responseData.totalRowCount, pageRowCount, this._registryId);
        });
    }
    getUnderlyingTableDataAsync(visualId, logicalTableId, ignoreAliases, ignoreSelection, includeAllColumns, columnsToIncludeById, maxRows, includeDataValuesOption, applyWorksheetFormatting) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getUnderlyingTableDataAsync',
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
            [api_internal_contract_js_1.ParameterId.LogicalTableId]: logicalTableId,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.IgnoreSelection]: ignoreSelection,
            [api_internal_contract_js_1.ParameterId.IncludeAllColumns]: includeAllColumns,
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.MaxRows]: this.getLimitedMaxRows(maxRows, this.getMaxRowLimit() + 1),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
            [api_internal_contract_js_1.ParameterId.ApplyWorksheetFormatting]: applyWorksheetFormatting,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetUnderlyingTableData, parameters).then((response) => {
            const responseData = response.result;
            return this.processResultsTable(responseData.data, false);
        });
    }
    getUnderlyingTableDataReaderAsync(visualId, logicalTableId, pageRowCount, ignoreAliases, ignoreSelection, includeAllColumns, columnsToIncludeById, includeDataValuesOption, applyWorksheetFormatting) {
        pageRowCount = this.getLimitedMaxRows(pageRowCount, this.getMaxRowLimit());
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getUnderlyingTableDataReaderAsync',
            [api_internal_contract_js_1.ParameterId.ViewDataTableCacheId]: this.getViewDataTableCacheId(),
            [api_internal_contract_js_1.ParameterId.VisualId]: visualId,
            [api_internal_contract_js_1.ParameterId.LogicalTableId]: logicalTableId,
            [api_internal_contract_js_1.ParameterId.IgnoreAliases]: ignoreAliases,
            [api_internal_contract_js_1.ParameterId.IgnoreSelection]: ignoreSelection,
            [api_internal_contract_js_1.ParameterId.IncludeAllColumns]: includeAllColumns,
            [api_internal_contract_js_1.ParameterId.ColumnsToIncludeById]: this.verifyIncludeColumnArray(columnsToIncludeById),
            [api_internal_contract_js_1.ParameterId.ShowDataTableFormat]: ExternalToInternalEnumMappings_1.ExternalToInternalEnumMappings.showDataTableFormatType.convert(includeDataValuesOption),
            [api_internal_contract_js_1.ParameterId.PageRowCount]: pageRowCount,
            [api_internal_contract_js_1.ParameterId.ApplyWorksheetFormatting]: applyWorksheetFormatting,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetUnderlyingTableDataReader, parameters).then((response) => {
            const responseData = response.result;
            return new GetDataTableReaderModels_1.DataTableReader(responseData.id, responseData.totalRowCount, pageRowCount, this._registryId);
        });
    }
    getPageAsync(tableId, pageNumber, pageSize) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'getPageAsync',
            [api_internal_contract_js_1.ParameterId.ViewDataTableCacheId]: this.getViewDataTableCacheId(),
            [api_internal_contract_js_1.ParameterId.ViewDataTableId]: tableId,
            [api_internal_contract_js_1.ParameterId.DataRowIndex]: pageNumber * pageSize,
        };
        return this.execute(api_internal_contract_js_1.VerbId.GetDataTableReaderPage, parameters).then((response) => {
            const responseData = response.result;
            return this.processResultsPage(responseData, tableId === GetDataServiceImpl.summaryTableId ? true : false);
        });
    }
    releaseAsync(tableId) {
        const parameters = {
            [api_internal_contract_js_1.ParameterId.FunctionName]: 'release',
            [api_internal_contract_js_1.ParameterId.ViewDataTableCacheId]: this.getViewDataTableCacheId(),
            [api_internal_contract_js_1.ParameterId.ViewDataTableId]: tableId,
        };
        return this.execute(api_internal_contract_js_1.VerbId.ReleaseDataTableReader, parameters).then((response) => {
            return;
        });
    }
    verifyIncludeColumnArray(columns) {
        // columns must be a valid array
        if (!Array.isArray(columns)) {
            throw new ApiShared_1.TableauError(api_external_contract_js_1.ErrorCodes.InvalidParameter, 'columnsToInclude and columnsToIncludeById must be valid arrays');
        }
        // Remove any duplicates from the input array
        const columnsAsSet = new Set(columns);
        return Array.from(columnsAsSet);
    }
    processResultsTable(responseData, isSummary) {
        const headers = responseData.headers.map((h) => new GetDataModels_1.Column(h.fieldCaption, h.fieldName, h.dataType, h.isReferenced, h.index));
        // TODO This should be controlled by a flag indicating whether this api will respond marks info or not
        let marks;
        if (responseData.marks) {
            marks = responseData.marks.map((h) => new GetDataModels_1.MarkInfo(h.type, h.color, h.tupleId));
        }
        // Limit+1 is our sentinal that underlying data contains more rows than user is allowed to fetch.
        // Remove the last element so we always return MaxRowLimit
        const isTotalRowCountLimited = isSummary === false && responseData.dataTable.length === this.getMaxRowLimit() + 1;
        if (isTotalRowCountLimited) {
            responseData.dataTable.length -= 1;
        }
        const table = responseData.dataTable.map((row) => {
            return row.map((cell, index) => {
                return DataValueFactory_1.DataValueFactory.MakeTableDataValue(cell, headers[index].dataType);
            });
        });
        if (marks) {
            return new GetDataModels_1.DataTable(table, headers, table.length, isTotalRowCountLimited, isSummary, marks);
        }
        return new GetDataModels_1.DataTable(table, headers, table.length, isTotalRowCountLimited, isSummary);
    }
    processResultsPage(responseData, isSummary) {
        const headers = responseData.headers.map((h) => new GetDataModels_1.Column(h.fieldCaption, h.fieldName, h.dataType, h.isReferenced, h.index));
        let marks;
        if (responseData.marks) {
            marks = responseData.marks.map((h) => new GetDataModels_1.MarkInfo(h.type, h.color, h.tupleId));
        }
        const table = responseData.dataTable.map((row) => {
            return row.map((cell, index) => {
                return DataValueFactory_1.DataValueFactory.MakeTableDataValue(cell, headers[index].dataType);
            });
        });
        if (marks) {
            return new GetDataModels_1.DataTable(table, headers, table.length, false, isSummary, marks);
        }
        return new GetDataModels_1.DataTable(table, headers, table.length, false, isSummary);
    }
}
exports.GetDataServiceImpl = GetDataServiceImpl;
GetDataServiceImpl.summaryTableId = '';
//# sourceMappingURL=GetDataServiceImpl.js.map